import { Button, Card } from 'antd'
import { CreditCardOutlined, LockOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAuthorization } from '@/contexts/AuthorizationContext'
import { permissions } from '@/types/authorization'
import type { AdvicePrepPaywallReason } from '../types'

const COPY: Record<AdvicePrepPaywallReason, { title: string; body: string; action: string }> = {
  signup_incomplete: {
    title: 'Finish setting up your subscription',
    body: 'Your account was created but the AdvicePrep subscription was never completed. Add a payment method to unlock this area.',
    action: 'Go to Billing',
  },
  past_due: {
    // Promising restoration is honest only because Billing pays the outstanding
    // invoice (5.9) rather than just attaching a card. If that pay step is ever
    // removed, this copy has to soften with it.
    title: 'Check your payment method',
    body: 'Your last AdvicePrep payment did not go through. Settle the outstanding invoice in Billing and access returns straight away.',
    action: 'Go to Billing',
  },
  subscription_ended: {
    title: 'Your AdvicePrep subscription has ended',
    body: 'Subscribe to a plan to use this area again. Your data is still here and comes back with the subscription.',
    action: 'Choose a plan',
  },
}

type AdvicePrepPaywallScreenProps = {
  reason: AdvicePrepPaywallReason
  areaLabel: string
}

export default function AdvicePrepPaywallScreen({ reason, areaLabel }: AdvicePrepPaywallScreenProps) {
  const navigate = useNavigate()
  const { hasPermission } = useAuthorization()
  const copy = COPY[reason]
  // Billing is the only way out of the paywall, and it is itself permissioned.
  // Sending someone who cannot reach it to /unauthorized would leave them with
  // nowhere to go at all.
  const canReachBilling = hasPermission(permissions.viewBilling)

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <Card className="w-full max-w-[34rem] rounded-2xl border border-brand-100 text-center shadow-card">
        <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-brand-50 text-2xl text-brand-600">
          <LockOutlined />
        </div>

        <div className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-400">{areaLabel}</div>
        <h1 className="mt-1 font-display text-xl font-bold text-gray-900">{copy.title}</h1>
        <p className="mx-auto mt-2 max-w-[26rem] text-sm leading-6 text-gray-600">
          {canReachBilling ? copy.body : `${copy.body} Ask an administrator at your firm to sort it out in Billing.`}
        </p>

        {canReachBilling && (
          <Button
            type="primary"
            size="large"
            icon={<CreditCardOutlined />}
            className="mt-6"
            onClick={() => navigate('/billing')}
          >
            {copy.action}
          </Button>
        )}
      </Card>
    </div>
  )
}
